import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildNewNoteRoute } from '../../utils/noteReturnContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useHaptics } from '../../hooks/useHaptics';
import { prefetchRoute } from '../../app/router/routePrefetch';
import { isClientUser } from '../../app/router/helpers';
import { usePermissions } from '../../hooks/usePermissions';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MicIcon from '@mui/icons-material/Mic';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MedicationIcon from '@mui/icons-material/Medication';
import PaymentIcon from '@mui/icons-material/Payment';
import SecurityIcon from '@mui/icons-material/Security';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';

/**
 * Mobile bottom navigation bar
 * iOS/Android style navigation with 4 primary actions
 * Enhanced with badges, haptic feedback, and long-press quick actions
 * Role-aware: shows different navigation items for client vs provider users
 */
function BottomNavBar({ user, unreadMessageCount = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [longPressItem, setLongPressItem] = useState(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef(null);
  const { trigger: triggerHaptic } = useHaptics();
  const { hasPermission } = usePermissions();

  const navRef = useRef<HTMLElement | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  // Track the user identity so we can reset nav state on character switch
  const prevUserIdRef = useRef(user?.uuid || user?.id);

  useEffect(() => {
    const currentId = user?.uuid || user?.id;
    if (prevUserIdRef.current && currentId && prevUserIdRef.current !== currentId) {
      // User identity changed (e.g. demo character switch) — reset nav state
      setMoreMenuOpen(false);
      setLongPressItem(null);
    }
    prevUserIdRef.current = currentId;
  }, [user?.uuid, user?.id, user?.character_type]);

  useEffect(() => {
    if (!isMobile) return;
    const onCalendar = /^\/calendar/.test(location.pathname);

    const show = () => { if (navRef.current) navRef.current.style.transform = 'translateY(0)'; };
    const hide = () => { if (navRef.current) navRef.current.style.transform = 'translateY(110%)'; };

    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    show();
    if (onCalendar) {
      hideTimerRef.current = setTimeout(hide, 2000);
    }

    const onPageScroll = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        show();
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        if (onCalendar) {
          hideTimerRef.current = setTimeout(hide, 2000);
        }
      });
    };

    window.addEventListener('scroll', onPageScroll, { passive: true });
    const main = document.querySelector('main');
    if (main) main.addEventListener('scroll', onPageScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onPageScroll);
      const mainEl = document.querySelector('main');
      if (mainEl) mainEl.removeEventListener('scroll', onPageScroll);
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile, location.pathname]);

  // Determine if user is a client to show appropriate navigation
  // Include character_type in the dependency to re-evaluate immediately on demo switch
  const isClient = useMemo(() => isClientUser(user), [user, user?.character_type, user?.role]);

  // Provider navigation items (therapists, admins, etc.)
  // Primary tabs: Calendar, (Notes if permitted), Clients
  // Everything else (Assistant, Messages, Billing, etc.) lives under "More"
  const canViewNotes = hasPermission('notes.view');
  const canCreateNotes = hasPermission('notes.create');
  const providerNavItems = useMemo(() => [
    {
      id: 'calendar',
      label: 'Calendar',
      icon: <CalendarMonthIcon />,
      path: '/calendar',
      activePattern: /^\/calendar/,
      quickActions: [
        { label: 'New Appointment', icon: <AddIcon />, action: () => navigate('/calendar?action=new') },
      ],
    },
    ...(canViewNotes ? [{
      id: 'notes',
      label: 'Notes',
      icon: <DescriptionIcon />,
      path: '/notes',
      activePattern: /^\/(notes)/,
      quickActions: [
        { label: 'New Note', icon: <AddIcon />, action: () => navigate(buildNewNoteRoute()) },
        ...(canCreateNotes ? [{ label: 'Drive Mode', icon: <MicIcon />, action: () => navigate('/drive-mode') }] : []),
      ],
    }] : []),
    {
      id: 'clients',
      label: 'Clients',
      icon: <PeopleIcon />,
      path: '/cases',
      activePattern: /^\/(clients|patients)/,
      quickActions: [],
    },
    {
      id: 'more',
      label: 'More',
      icon: <MoreHorizIcon />,
      isMoreMenu: true,
      activePattern: /^\/(dashboard|settings|profile|vault|library|projects|todo|tasks|billing|drive-mode|analytics|ai-assistant|assistant|messages|my-caseload)/,
      menuItems: [
        { type: 'section', label: 'Daily' },
        { label: 'Dashboard', icon: <CalendarMonthIcon />, path: '/dashboard' },
        { label: 'Messages', icon: <MailOutlineIcon />, path: '/messages', badge: unreadMessageCount > 0 ? unreadMessageCount : null },
        { label: 'Tasks', icon: <FolderIcon />, path: '/tasks' },
        { type: 'section', label: 'Clinical' },
        { label: 'Assistant', icon: <ChatBubbleOutlineIcon />, path: '/ai-assistant' },
        { label: 'Projects', icon: <DescriptionIcon />, path: '/projects' },
        { label: 'Library', icon: <FolderIcon />, path: '/library' },
        ...(canCreateNotes ? [{ label: 'Drive Mode', icon: <MicIcon />, path: '/drive-mode' }] : []),
        { label: 'Billing', icon: <PaymentIcon />, path: '/billing' },
        { label: 'My Caseload', icon: <PeopleIcon />, path: '/my-caseload' },
        { type: 'section', label: 'Tools' },
        { label: 'Analytics', icon: <TrendingUpIcon />, path: '/analytics' },
        { type: 'section', label: 'Account' },
        { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
      ],
    },
  ], [navigate, unreadMessageCount, canViewNotes, canCreateNotes]);

  // Client navigation items (patients, parents, couples)
  // Shows a subset of the ClientPortalPage tabs as primary items (Dashboard, Messages, Health) with the rest under More
  const clientNavItems = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/client?tab=dashboard',
      activePattern: /^\/client(\?.*tab=dashboard|$)/,
      quickActions: [],
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: <MailOutlineIcon />,
      path: '/client?tab=messages',
      activePattern: /^\/client(\/messages|\?.*tab=messages)/,
      badge: unreadMessageCount > 0 ? unreadMessageCount : null,
      quickActions: [],
    },
    {
      id: 'health',
      label: 'Health',
      icon: <TrendingUpIcon />,
      path: '/client?tab=health',
      activePattern: /^\/client(\?.*tab=health)/,
      quickActions: [],
    },
    {
      id: 'more',
      label: 'More',
      icon: <MoreHorizIcon />,
      isMoreMenu: true,
      activePattern: /^\/client\?.*tab=(appointments|paperwork|summaries|medications|notifications|payments|consent|careteam|resources|settings)/,
      menuItems: [
        { type: 'section', label: 'Care' },
        { label: 'Appointments', icon: <EventIcon />, path: '/client?tab=appointments' },
        { label: 'Paperwork', icon: <DescriptionIcon />, path: '/client?tab=paperwork' },
        { type: 'section', label: 'Health' },
        { label: 'Session Summaries', icon: <SummarizeIcon />, path: '/client?tab=summaries' },
        { label: 'Medications', icon: <MedicationIcon />, path: '/client?tab=medications' },
        { type: 'section', label: 'Admin' },
        { label: 'Notifications', icon: <NotificationsIcon />, path: '/client?tab=notifications' },
        { label: 'Payments', icon: <PaymentIcon />, path: '/client?tab=payments' },
        { label: 'Consent', icon: <SecurityIcon />, path: '/client?tab=consent' },
        { label: 'Care Team', icon: <PeopleIcon />, path: '/client?tab=careteam' },
        { label: 'Resources', icon: <FolderIcon />, path: '/client?tab=resources' },
        { type: 'section', label: 'Account' },
        { label: 'Settings', icon: <SettingsIcon />, path: '/client?tab=settings' },
      ],
    },
  ], [navigate, unreadMessageCount]);

  // Select navigation items based on user role
  const navItems = isClient ? clientNavItems : providerNavItems;

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  // Early returns after all hooks
  if (!isMobile || !user) {
    return null;
  }

  const hideOnPaths = ['/login', '/demo-login', '/register', '/forgot-password', '/reset-password'];
  if (hideOnPaths.includes(location.pathname)) {
    return null;
  }

  const isActive = (item) => {
    const fullPath = location.pathname + location.search;
    return item.activePattern.test(fullPath);
  };

  const handleNavClick = (item) => {
    triggerHaptic('light');
    if (item.isMoreMenu) {
      setMoreMenuOpen(prev => !prev);
      return;
    }
    setMoreMenuOpen(false);
    navigate(item.path);
  };

  const handleTouchStart = (item, e) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    
    // Prefetch the route chunk on touch so it's ready by the time the tap completes
    if (item.path) {
      prefetchRoute(item.path);
    }

    if (item.quickActions && item.quickActions.length > 0) {
      longPressTimerRef.current = setTimeout(() => {
        triggerHaptic('heavy');
        setLongPressItem(item);
      }, 500);
    }
  };

  const handleTouchMove = (e) => {
    if (!touchStartRef.current) return;
    
    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    
    if (deltaX > 10 || deltaY > 10) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  };

  const handleQuickAction = (action) => {
    triggerHaptic('medium');
    setLongPressItem(null);
    action();
  };

  return (
    <>
      <nav
        ref={navRef}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-bottom"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transform: 'translateY(0)',
          transition: 'transform 250ms ease',
          willChange: 'transform',
        }}
        role="navigation"
        aria-label="Mobile bottom navigation"
      >
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                onTouchStart={(e) => handleTouchStart(item, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`relative flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                  active
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
              >
                <div className={`relative text-2xl mb-1 ${active ? 'scale-110' : ''} transition-transform`}>
                  {item.icon}
                  {item.badge && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium ${active ? 'font-semibold' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Menu for Client Portal and Provider */}
      {moreMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setMoreMenuOpen(false)}
          />
          <div
            className="fixed bottom-20 left-0 right-0 z-[70] px-4"
            style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">More</h3>
              </div>
              <div className="py-2 max-h-[60vh] overflow-y-auto">
                {navItems.find(i => i.id === 'more')?.menuItems?.map((menuItem, idx) => (
                  menuItem.type === 'section' ? (
                    <div
                      key={`section-${menuItem.label}`}
                      className={`px-4 pt-3 pb-1 ${idx > 0 ? 'border-t border-gray-100 dark:border-gray-700 mt-1' : ''}`}
                    >
                      <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        {menuItem.label}
                      </span>
                    </div>
                  ) : (
                    <button
                      key={menuItem.label}
                      onClick={() => {
                        triggerHaptic('medium');
                        setMoreMenuOpen(false);
                        navigate(menuItem.path);
                      }}
                      onTouchStart={() => menuItem.path && prefetchRoute(menuItem.path)}
                      className="w-full flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px]"
                    >
                      <div className="text-blue-500 mr-3">{menuItem.icon}</div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{menuItem.label}</span>
                    </button>
                  )
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Quick Actions Menu */}
      {longPressItem && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
            onClick={() => setLongPressItem(null)}
          />

          {/* Quick Actions */}
          <div 
            className="fixed bottom-20 left-0 right-0 z-[70] px-4"
            style={{
              marginBottom: 'env(safe-area-inset-bottom, 0px)',
            }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {longPressItem.label} Quick Actions
                </h3>
              </div>
              <div className="py-2">
                {longPressItem.quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(action.action)}
                    className="w-full flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="text-blue-500 mr-3">
                      {action.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {action.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default BottomNavBar;
